import React, { useState, useEffect, useRef, useMemo } from "react";
import { Calendar } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad2 = (n) => String(n).padStart(2, "0");

/** Stored value: `YYYY-MM-DDTHH:mm` (local). */
function parseExpiryValue(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (trimmed.length === 10 && trimmed.includes("-")) {
    const [y, m, d] = trimmed.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 0, 0);
  }
  if (trimmed.length >= 16 && trimmed.includes("T")) {
    const [dPart, tPart] = trimmed.split("T");
    const [y, m, day] = dPart.split("-").map(Number);
    const [hh, mm] = (tPart || "23:59").slice(0, 5).split(":").map(Number);
    return new Date(y, m - 1, day, hh, mm, 0, 0);
  }
  const fromIso = new Date(trimmed);
  return Number.isNaN(fromIso.getTime()) ? null : fromIso;
}

function toStoredString(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function displayDateOnly(value) {
  const d = parseExpiryValue(value);
  if (!d) return "Select date";
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function buildCalendarCells(year, month) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevLast = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstDow; i++) {
    const day = prevLast - firstDow + i + 1;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    cells.push({ day, muted: true, m: pm, y: py });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, muted: false, m: month, y: year });
  }
  let nextDay = 1;
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  while (cells.length < 42) {
    cells.push({ day: nextDay++, muted: true, m: nm, y: ny });
  }
  return cells;
}

export default function ExpiresAtPicker({ value, onChange, error }) {
  const safeValue = value ?? "";
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = useMemo(() => parseExpiryValue(safeValue), [safeValue]);
  const viewDate = selected || new Date();
  const [cursorMonth, setCursorMonth] = useState(() =>
    new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)
  );

  useEffect(() => {
    if (open && selected) {
      setCursorMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
    }
  }, [open, selected]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const year = cursorMonth.getFullYear();
  const month = cursorMonth.getMonth();
  const cells = useMemo(
    () => buildCalendarCells(year, month),
    [year, month]
  );

  const defaultTimeIfNone = () => {
    if (selected) {
      return { h: selected.getHours(), min: selected.getMinutes() };
    }
    const now = new Date();
    return { h: now.getHours(), min: now.getMinutes() };
  };

  const pickDay = (cell) => {
    const { h, min } = defaultTimeIfNone();
    const next = new Date(cell.y, cell.m, cell.day, h, min, 0, 0);
    onChange(toStoredString(next));
    setOpen(false);
  };

  const goMonth = (delta) => {
    setCursorMonth(new Date(year, month + delta, 1));
  };

  const applyTimeString = (t) => {
    if (t == null || t === "") return;
    const [hh, mm] = String(t).split(":").map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return;
    if (selected) {
      const next = new Date(selected);
      next.setHours(hh, mm, 0, 0);
      onChange(toStoredString(next));
      return;
    }
    const today = new Date();
    const next = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hh,
      mm,
      0,
      0
    );
    onChange(toStoredString(next));
  };

  const handleTimeChange = (e) => applyTimeString(e.target.value);

  const timeInputValue = selected
    ? `${pad2(selected.getHours())}:${pad2(selected.getMinutes())}`
    : "";

  const borderCls = error
    ? "border-red-500/60"
    : isDarkMode
      ? "border-zinc-600 hover:border-zinc-500"
      : "border-gray-300 hover:border-gray-400";

  const triggerSurface = isDarkMode
    ? "bg-zinc-800/80 text-white"
    : "bg-white text-gray-900";
  const triggerPlaceholder = isDarkMode ? "text-zinc-500" : "text-gray-500";

  const popoverSurface = isDarkMode
    ? "bg-zinc-900 border-zinc-700 shadow-black/40"
    : "bg-white border-zinc-200";
  const popoverHeader = isDarkMode ? "text-zinc-100" : "text-zinc-900";
  const navBtn = isDarkMode
    ? "hover:bg-zinc-800 text-zinc-200"
    : "hover:bg-zinc-100 text-zinc-800";
  const weekdays = isDarkMode ? "text-zinc-400" : "text-zinc-600";
  const dayMuted = isDarkMode ? "text-zinc-600" : "text-zinc-400";
  const dayNormal = isDarkMode ? "text-zinc-100" : "text-zinc-900";
  const dayHoverMuted = isDarkMode ? "hover:bg-zinc-800/80" : "hover:bg-zinc-50";
  const dayHover = isDarkMode ? "hover:bg-zinc-800" : "hover:bg-zinc-100";
  const footerBorder = isDarkMode ? "border-zinc-700" : "border-zinc-100";
  const linkBtn = isDarkMode
    ? "text-blue-400 hover:text-blue-300"
    : "text-[#2563eb] hover:underline";

  const subLabel = isDarkMode ? "text-zinc-400" : "text-gray-600";
  const timeField = isDarkMode
    ? "bg-zinc-800/80 text-white scheme-dark"
    : "bg-white text-gray-900";

  const isSameDay = (a, cy, cm, cd) =>
    a &&
    a.getFullYear() === cy &&
    a.getMonth() === cm &&
    a.getDate() === cd;

  return (
    <div ref={wrapRef} className="relative w-full space-y-3">
      <div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border text-left transition-colors ${triggerSurface} ${borderCls}`}
        >
          <span className={safeValue ? "" : triggerPlaceholder}>
            {displayDateOnly(safeValue)}
          </span>
          <Calendar
            className={`w-4 h-4 shrink-0 ${isDarkMode ? "text-zinc-400" : "text-gray-500"}`}
          />
        </button>

        {open && (
          <div className="absolute z-[100] left-0 right-0 mt-2 flex justify-center sm:justify-start">
            <div
              className={`rounded-lg shadow-2xl border overflow-hidden min-w-[280px] max-w-full ${popoverSurface}`}
            >
              <div className="p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <button
                    type="button"
                    className={`p-1.5 rounded text-lg leading-none ${navBtn}`}
                    onClick={() => goMonth(-1)}
                    aria-label="Previous month"
                  >
                    ↑
                  </button>
                  <span className={`text-sm font-semibold ${popoverHeader}`}>
                    {MONTHS[month]}, {year}
                  </span>
                  <button
                    type="button"
                    className={`p-1.5 rounded text-lg leading-none ${navBtn}`}
                    onClick={() => goMonth(1)}
                    aria-label="Next month"
                  >
                    ↓
                  </button>
                </div>
                <div
                  className={`grid grid-cols-7 gap-0.5 text-center text-[10px] sm:text-xs mb-1 ${weekdays}`}
                >
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                    <div key={d} className="py-1 font-medium">
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
                  {cells.map((cell, idx) => {
                    const inViewMonth = cell.m === month && cell.y === year;
                    const isSelected =
                      selected && isSameDay(selected, cell.y, cell.m, cell.day);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => pickDay(cell)}
                        className={`
                        w-8 h-8 sm:w-9 sm:h-9 rounded text-sm flex items-center justify-center mx-auto
                        ${
                          isSelected
                            ? "bg-[#2563eb] text-white font-semibold ring-2 ring-[#1d4ed8]"
                            : inViewMonth
                              ? `${dayNormal} ${dayHover}`
                              : `${dayMuted} ${dayHoverMuted}`
                        }
                      `}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
                <div
                  className={`flex justify-between mt-3 pt-2 border-t ${footerBorder}`}
                >
                  <button
                    type="button"
                    className={`text-sm font-medium ${linkBtn}`}
                    onClick={() => {
                      onChange("");
                      setOpen(false);
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className={`text-sm font-medium ${linkBtn}`}
                    onClick={() => {
                      const n = new Date();
                      if (selected) {
                        const next = new Date(
                          n.getFullYear(),
                          n.getMonth(),
                          n.getDate(),
                          selected.getHours(),
                          selected.getMinutes(),
                          0,
                          0
                        );
                        onChange(toStoredString(next));
                      } else {
                        onChange(toStoredString(n));
                      }
                    }}
                  >
                    Today
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <label
          className={`text-xs font-semibold block mb-1.5 ${subLabel}`}
          htmlFor="coupon-expires-time"
        >
          Expire time
        </label>
        <input
          id="coupon-expires-time"
          type="time"
          step={60}
          value={timeInputValue}
          onChange={handleTimeChange}
          onInput={handleTimeChange}
          className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${timeField} ${borderCls} ${
            isDarkMode
              ? "focus:ring-orange-400/40 focus:border-orange-400/50"
              : "focus:ring-orange-500/30 focus:border-orange-500"
          }`}
        />
      </div>
    </div>
  );
}
